// =============================================================================
// domain/riskAdjusted.ts — Sharpe, Sortino and Calmar on a trading book.
// -----------------------------------------------------------------------------
// Three ratios that ask the same question three ways: **is the return worth the
// pain it cost?** Each is computed on DAILY P&L over the days actually traded,
// which is the standard convention — a day flat on the beach is not a data point
// about the strategy.
//
//   Sharpe    mean daily P&L ÷ the deviation of ALL of it
//   Sortino   the same, but only DOWNSIDE deviation — upside volatility is not
//             a risk anyone needs protecting from
//   Calmar    annualised return ÷ the worst drawdown — return over pain, direct
//
// ── 🚨 THE "GOOD SIGN" THE OLD CLIENT PRINTS ON A LOSING BOOK ──
//
// Its verdict fires "Sortino noticeably higher than Sharpe — your variance is
// dominated by upside, not downside (good sign)" whenever `sortino > sharpe *
// 1.5`. That reading is derived for POSITIVE ratios, and the test is arithmetic:
// with a losing book both ratios are negative, `-1.41 > -1.98` is true, and the
// sentence prints.
//
// It is not merely unhelpful, it is guaranteed. Downside deviation is never
// larger than total deviation, so on a negative mean the Sortino is always at
// least as negative as the Sharpe — the comparison carries no information about
// a losing book at all. **And it crowds out the message that should appear**:
// a sub-0.5 Sharpe means the returns are not paying for the volatility.
//
// Josh's own book: −$3,278, Sharpe −1.32, Sortino −1.41, and the old client
// tells him it is a good sign. PD-19.
//
// ── ⚠ NO WALL CLOCK, NO STORE (D26) ──
// The caller resolves each trade's effective date and hands it in.
// =============================================================================

/** Fewer trading days than this and neither deviation means anything. */
export const MIN_TRADING_DAYS = 5;
/** Calmar annualises, so it needs a span of history rather than a day count. */
export const MIN_DAYS_FOR_CALMAR = 14;
/** The conventional annualiser — trading days, not calendar days. */
export const TRADING_DAYS_PER_YEAR = 252;

export type RiskTier = 'strong' | 'good' | 'marginal' | 'weak' | 'ungraded';

export interface RatioReading {
  value: number | null;
  tier: RiskTier;
  /**
   * ⚠ TRUE when the ratio is arithmetically real and analytically noise — an
   * equity curve so smooth that the deviation underneath it is near zero. A
   * Sharpe of 40 is not a triumph, it is a tell about the sample.
   */
  degenerate: boolean;
}

export interface RiskAdjusted {
  sharpe: RatioReading;
  sortino: RatioReading;
  calmar: RatioReading;
  /** Days with at least one trade. The population for Sharpe and Sortino. */
  tradingDays: number;
  /** First to last trading day, inclusive, in calendar days. Calmar's base. */
  daysSpanned: number;
  verdict: RiskVerdict;
}

export type RiskVerdict =
  /** The sample cannot support the ratios at all. */
  | { kind: 'too-few-days'; tradingDays: number }
  /** The curve is too smooth for the ratios to mean anything. */
  | { kind: 'degenerate' }
  /** Returns are not paying for the volatility. */
  | { kind: 'not-compensating'; tier: RiskTier }
  /** Genuinely upside-dominant variance. ⚠ Only reachable on a POSITIVE Sharpe. */
  | { kind: 'upside-dominant'; tier: RiskTier; sharpe: number; sortino: number }
  /** Deviation is roughly symmetric around the mean day. */
  | { kind: 'symmetric'; tier: RiskTier };

/**
 * Above these a ratio is an artefact of a near-flat or near-monotonic curve.
 * Ported as the old client set them, including its reasoning: a Sharpe over 6 or
 * a Calmar over 15 on discretionary futures is what a demo dataset or a short
 * low-drawdown streak produces, not what an edge produces.
 */
const CEILING = { sharpe: 6, sortino: 9, calmar: 15 } as const;

const TIERS = {
  sharpe: { strong: 2, good: 1, marginal: 0.5 },
  sortino: { strong: 2.5, good: 1.5, marginal: 0.7 },
  calmar: { strong: 5, good: 3, marginal: 1 },
} as const;

type RatioKind = keyof typeof CEILING;

function read(kind: RatioKind, value: number | null): RatioReading {
  if (value == null) return { value: null, tier: 'ungraded', degenerate: false };
  if (Math.abs(value) > CEILING[kind]) {
    // ⚠ The VALUE is withheld too, not just the grade. Printing "Sharpe 41.2 —
    // n/a" invites the reader to keep the number and discard the caveat, which
    // is how a figure whose caveat was computed and dropped gets quoted.
    return { value: null, tier: 'ungraded', degenerate: true };
  }
  const t = TIERS[kind];
  const tier: RiskTier = value >= t.strong ? 'strong'
    : value >= t.good ? 'good'
      : value >= t.marginal ? 'marginal' : 'weak';
  return { value, tier, degenerate: false };
}

export interface RiskTrade {
  /** The EFFECTIVE date — resolved by the caller. */
  date: string | null;
  netPnl: number | null;
}

/**
 * The three ratios, and a verdict that does not contradict them.
 *
 * @param maxDrawdown POSITIVE magnitude of the worst peak-to-trough.
 * @param periodPnl   the period's net, which Calmar annualises.
 *
 * ⚠ DAILY BUCKETS OVER THE DAYS TRADED. Zero-filling the idle days would drag
 * the mean toward zero and shrink the deviation at the same time, which moves
 * both ratios in the flattering direction on a trader who trades twice a week.
 */
export function riskAdjusted(
  trades: readonly RiskTrade[], maxDrawdown: number, periodPnl: number,
): RiskAdjusted {
  const daily = new Map<string, number>();
  for (const t of trades) {
    const d = t.date;
    if (!d) continue;
    daily.set(d, (daily.get(d) ?? 0) + (Number(t.netPnl) || 0));
  }
  const returns = [...daily.values()];
  const tradingDays = returns.length;

  const dates = [...daily.keys()].sort();
  const daysSpanned = dates.length >= 2
    ? Math.max(1, daysBetweenInclusive(dates[0] as string, dates[dates.length - 1] as string))
    : dates.length;

  let sharpe: number | null = null;
  let sortino: number | null = null;
  if (tradingDays >= MIN_TRADING_DAYS) {
    const mean = returns.reduce((s, r) => s + r, 0) / tradingDays;
    // ⚠ POPULATION variance (÷ n), which is the old client's choice and the one
    // that keeps the two deviations comparable — the downside figure below is
    // divided by the same n, not by the count of losing days.
    const variance = returns.reduce((s, r) => s + (r - mean) ** 2, 0) / tradingDays;
    const std = Math.sqrt(variance);
    // ⚠ SEMI-DEVIATION AROUND ZERO, not around the mean. The target return is a
    // flat day: a trader is not exposed by having had a smaller-than-average
    // green day, and measuring against the mean would say they were.
    const downside = Math.sqrt(returns.reduce((s, r) => s + (r < 0 ? r * r : 0), 0) / tradingDays);
    const annualiser = Math.sqrt(TRADING_DAYS_PER_YEAR);
    if (std > 0) sharpe = (mean / std) * annualiser;
    if (downside > 0) sortino = (mean / downside) * annualiser;
  }

  let calmar: number | null = null;
  if (daysSpanned >= MIN_DAYS_FOR_CALMAR && maxDrawdown > 0 && periodPnl !== 0) {
    calmar = (periodPnl * (365 / daysSpanned)) / maxDrawdown;
  }

  const s = read('sharpe', sharpe);
  const so = read('sortino', sortino);
  const c = read('calmar', calmar);

  return {
    sharpe: s, sortino: so, calmar: c,
    tradingDays, daysSpanned,
    verdict: verdictFor(s, so, c, tradingDays),
  };
}

/**
 * 🚨 THE ORDER IS THE FIX.
 *
 * The old client tests `sortino > sharpe * 1.5` FIRST and unconditionally. On a
 * losing book that comparison is true by arithmetic — both ratios are negative
 * and the Sortino is the more negative one, so multiplying the Sharpe by 1.5
 * pushes it further down than the Sortino ever is. The "good sign" prints, and
 * it displaces the sentence a trader with a sub-0.5 Sharpe actually needs.
 *
 * Here the upside-dominant reading requires a **positive** Sharpe, because that
 * is the only regime in which "downside deviation is smaller than total
 * deviation" is information rather than a tautology.
 */
export function verdictFor(
  sharpe: RatioReading, sortino: RatioReading, calmar: RatioReading, tradingDays: number,
): RiskVerdict {
  if (sharpe.degenerate || sortino.degenerate || calmar.degenerate) return { kind: 'degenerate' };
  if (tradingDays < MIN_TRADING_DAYS) return { kind: 'too-few-days', tradingDays };
  if (sharpe.value == null || sortino.value == null) return { kind: 'too-few-days', tradingDays };

  const sh = sharpe.value;
  const so = sortino.value;
  // 🚨 THE ORDER IS THE ENTIRE FIX. Checking this FIRST is what keeps the
  // upside-dominant reading out of the negative regime, because a losing book's
  // Sharpe is always below the marginal bar and returns here.
  //
  // ⚠ A `sh > 0 &&` guard on the branch below would look like the protection and
  // would be DEAD — by this line `sh >= 0.5` already. A deliberate sabotage
  // removing it changed nothing, which is how it was found. Deleted rather than
  // kept: a line that cannot change the outcome reads as the thing producing it,
  // and the next person to move these branches would trust the wrong mechanism.
  if (sh < TIERS.sharpe.marginal) return { kind: 'not-compensating', tier: sharpe.tier };
  if (so > sh * 1.5) {
    return { kind: 'upside-dominant', tier: sharpe.tier, sharpe: sh, sortino: so };
  }
  return { kind: 'symmetric', tier: sharpe.tier };
}

/** Inclusive span in calendar days. ⚠ Parsed at local midnight, as elsewhere. */
function daysBetweenInclusive(fromIso: string, toIso: string): number {
  const a = new Date(`${fromIso}T00:00:00`).getTime();
  const b = new Date(`${toIso}T00:00:00`).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
  return Math.round((b - a) / 86_400_000) + 1;
}
